import React from 'react';
import { cn } from '../lib/utils';
import { Card, CardContent } from './ui/card';
import Reveal from './Reveal';
import IconBadge from './IconBadge';
import EditableText from './EditableText';

/**
 * Numbered step cards with IconBadge — matches HowItWorks visual language.
 * If `keyPrefix` is set, each step uses `${keyPrefix}.{n}.title` / `.text`.
 */
const StepCards = ({ steps, columns = 3, className, keyPrefix }) => {
  const gridClass =
    columns === 4
      ? 'sm:grid-cols-2 lg:grid-cols-4'
      : columns === 2
      ? 'sm:grid-cols-2'
      : 'md:grid-cols-3';

  return (
    <div className={cn('grid gap-6 lg:gap-8', gridClass, className)}>
      {steps.map((step, i) => {
        const StepIcon = step.icon;
        const n = i + 1;
        const titleKey = step.titleKey || (keyPrefix ? `${keyPrefix}.${n}.title` : null);
        const textKey = step.descriptionKey || step.textKey || (keyPrefix ? `${keyPrefix}.${n}.text` : null);
        return (
          <Reveal key={step.title} animation="fade-up" delay={i * 100}>
            <Card className="bg-white border-slate-200 shadow-sm text-center relative card-hover-glow hover:border-[rgba(58,0,224,0.28)] h-full">
              <CardContent className="pt-12 pb-8">
                <div className="relative inline-flex mb-5">
                  <IconBadge
                    icon={StepIcon}
                    variant={i % 2 === 0 ? 'purple' : 'orange'}
                    size="lg"
                  />
                  <span className="absolute -top-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-white border border-[rgba(58,0,224,0.3)] text-xs font-bold text-[#3a00e0] shadow-sm">
                    {step.number ?? n}
                  </span>
                </div>
                {titleKey ? (
                  <EditableText
                    editKey={titleKey}
                    defaultValue={step.title}
                    as="h3"
                    className="text-slate-900 font-semibold text-lg mb-2"
                  />
                ) : (
                  <h3 className="text-slate-900 font-semibold text-lg mb-2">{step.title}</h3>
                )}
                {textKey ? (
                  <EditableText
                    editKey={textKey}
                    defaultValue={step.description}
                    as="p"
                    multiline
                    className="text-slate-600 text-sm leading-relaxed"
                  />
                ) : (
                  <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
                )}
              </CardContent>
            </Card>
          </Reveal>
        );
      })}
    </div>
  );
};

export default StepCards;
